import React from "react";
import { useSortBy, useTable, usePagination, useExpanded } from "react-table";
import "./StatTable.css";
import Button from "../Button";
import { pageRangeText } from "./pageRange";

const defaultPropGetter = () => ({});

export default function StatTable({
  data,
  columns,
  paginate,
  showDKPlayerInfo,
  renderRowSubComponent,
  getRowProps = defaultPropGetter,
  getCellProps = defaultPropGetter,
  heatMap = false,
  initialState = {},
  pagination = "default",
  interactiveCells = true,
  autoResetSortBy = true,
  maxRows = undefined,
}) {
  const heatMapValues = {};

  columns.forEach((column) => {
    const accessor = column.accessor;

    let columnMax = Number.MIN_VALUE;
    let columnMin = Number.MAX_VALUE;

    data.forEach((row) => {
      const val = parseFloat(row[accessor]);

      if (!isNaN(val)) {
        if (val < columnMin) {
          columnMin = val;
        }

        if (val > columnMax) {
          columnMax = val;
        }
      }
    });

    if (!(columnMin === Number.MAX_VALUE || columnMax === Number.MIN_VALUE)) {
      heatMapValues[column.Header] = {
        columnMax: columnMax,
        columnMin: columnMin,
      };
    }
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    pageCount,
    rows,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    prepareRow,
    visibleColumns,
    state: { pageIndex, pageSize, expanded },
  } = useTable(
    {
      columns: columns,
      data: data,
      defaultColumn: {
        sortDescFirst: true,
      },
      manualSortBy: false,
      disableMultiSort: true,
      autoResetSortBy,
      style: { height: "50%" },
      initialState: { pageSize: 25, ...initialState },
      getCellProps: getCellProps,
    },
    useSortBy,
    useExpanded,
    paginate ? usePagination : ""
  );

  const visibleRowList = paginate ? page : rows;
  const renderedRows =
    typeof maxRows === "number" ? visibleRowList.slice(0, maxRows) : visibleRowList;

  return (
    <div className="stat-table-container">
      <table {...getTableProps()} className="stat-table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  key={column}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={
                    "stat-table-header noselect " +
                    (column.isSorted
                      ? column.isSortedDesc
                        ? "header-sort-down"
                        : "header-sort-up"
                      : "")
                  }
                  style={{}}
                >
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {renderedRows.map((row, index) => {
            prepareRow(row);
            return (
              <React.Fragment key={index}>
                <tr
                  onClick={() => row.toggleRowExpanded()}
                  style={{
                    cursor: renderRowSubComponent ? "default" : "default",
                  }}
                  title={row.original.rowTitle}
                  {...row.getRowProps(getRowProps(row))}
                >
                  {row.cells.map((cell) => {
                    let columnMax = null;
                    let columnMin = null;
                    let columnDiff = null;

                    if (cell.column.Header in heatMapValues) {
                      columnMax = heatMapValues[cell.column.Header].columnMax;
                      columnMin = heatMapValues[cell.column.Header].columnMin;

                      columnDiff = columnMax - columnMin;
                    }

                    let cellHue = ((cell.value - columnMin) / columnDiff) * 120;

                    if (!cell.column.sortDescFirst) {
                      cellHue = 120 - cellHue;
                    }

                    return (
                      <td
                        key={cell}
                        className={
                          cell.column.Header === "Name"
                            ? cell.row.original.dkPlayerId
                              ? "stat-table-dk-selectable-cell"
                              : null
                            : null
                        }
                        style={
                          heatMap && columnMax && columnMin
                            ? {
                                backgroundColor: `hsl(${cellHue}, 100%, 67%)`,
                              }
                            : null
                        }
                        {...cell.getCellProps(getCellProps(cell))}
                      >
                        {interactiveCells ? (
                          <span
                            onClick={() => {
                              cell.column.Header === "Name"
                                ? showDKPlayerInfo(cell.row.original.dkPlayerId)
                                : null;
                            }}
                            className={
                              cell.column.Header === "Name"
                                ? cell.row.original.dkPlayerId
                                  ? "stat-table-dk-selectable"
                                  : null
                                : null
                            }
                            onKeyPress={() => {
                              cell.column.Header === "Name"
                                ? showDKPlayerInfo(cell.row.original.dkPlayerId)
                                : null;
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {cell.render("Cell")}
                          </span>
                        ) : (
                          cell.render("Cell")
                        )}
                      </td>
                    );
                  })}
                </tr>
                {row.isExpanded && renderRowSubComponent ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      {renderRowSubComponent({ row })}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {pagination === "compact" ? null : <hr />}
      {paginate && pagination === "compact" ? (
        <div className="stat-table-pager">
          <span className="stat-table-pager-range">
            {pageRangeText(pageIndex, pageSize, rows.length)}
          </span>
          <div className="stat-table-pager-buttons">
            <button
              type="button"
              className="stat-table-pager-button"
              onClick={previousPage}
              disabled={!canPreviousPage}
              aria-label="Previous page"
            >
              ‹
            </button>
            <button
              type="button"
              className="stat-table-pager-button"
              onClick={nextPage}
              disabled={!canNextPage}
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        </div>
      ) : null}
      {paginate && pagination !== "compact" ? (
        <div className="stat-table-pagination-container">
          <Button form={true} onClick={previousPage}>
            Prev
          </Button>
          <div className="stat-table-pagination-settings">
            <p>
              Page {pageIndex + 1} of {pageCount}
            </p>
            {/* TODO: Get results to display <div>Results</div>*/}
          </div>
          <Button form={true} onClick={nextPage}>
            Next
          </Button>
        </div>
      ) : null}
    </div>
  );
}
